import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Avatar,
  IconButton,
  AppBar,
  Toolbar,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Collapse,
  Fab,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  GraduationCap,
  LogIn,
  LayoutDashboard,
  BookOpen,
  Megaphone,
  Calendar,
  FolderOpen,
  ArrowRight,
  Building2,
  X,
  Menu as MenuIcon,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  Pin,
  Search,
  Download,
  Sliders,
  ExternalLink,
  Link as LinkIcon,
  MapPin,
  Navigation,
  FileQuestion,
  Award,
  Phone,
  Mail,
  Compass,
  Users,
  Home,
  Utensils,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { Footer } from '../components/Footer';
import { DEFAULT_LANDING_MODULES, LandingModule } from './LandingPageManager';



const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = Boolean(token && user?.id);
  const isStaffOrAdmin = isLoggedIn && (user?.role === 'ADMIN' || user?.role === 'STAFF');

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isSliderHovered, setIsSliderHovered] = useState(false);
  // Track loaded slides to optimize memory and network bandwidth (lazy load non-initial slides)
  const [loadedSlideIndices, setLoadedSlideIndices] = useState<number[]>([0, 1]);

  // Touch Swipe State for Mobile Image Slider
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Modals & Filters
  const [schoolsModalOpen, setSchoolsModalOpen] = useState(false);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [schoolDetailsLoading, setSchoolDetailsLoading] = useState(false);

  // Partner Schools Module In-Card Search
  const [moduleSchoolSearch, setModuleSchoolSearch] = useState('');

  // Resource Filters & Modal
  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState<string>('ALL');
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');

  // Student Support Services Dropdown
  const [supportMenuAnchor, setSupportMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileSupportOpen, setMobileSupportOpen] = useState(false);

  // Selected Event Details Modal
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const [portalData, setPortalData] = useState<any>({
    stats: {
      students: 0,
      schools: 0,
      supervisors: 0,
      researches: 0,
      current_academic_year: '2569',
      current_semester: '1',
      demand_survey_open: false,
    },
    landing_page_modules: null,
    announcements: [],
    events: [],
    resources: [],
    schools: [],
  });

  const resolveFileUrl = (url?: string) => {
    if (!url) return '';
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace(/^http:\/\//i, 'https://');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
    return url;
  };

  useEffect(() => {
    document.title =
      'TPMS KPRU | ระบบบริหารจัดการงานฝึกประสบการณ์วิชาชีพครู - Teacher Practicum Management System (TPMS) คณะครุศาสตร์ มรภ.กำแพงเพชร';
    const fetchPortal = async () => {
      try {
        const [portalRes, schRes] = await Promise.all([
          api.get('/research/public').catch(() => ({ data: {} })),
          api.get('/schools/public').catch(() => ({ data: [] }))
        ]);

        let portalSchools = Array.isArray(schRes.data) && schRes.data.length > 0
          ? schRes.data
          : (portalRes.data?.schools || []);

        if (portalRes.data && typeof portalRes.data === 'object') {
          setPortalData((prev: any) => ({
            ...prev,
            ...portalRes.data,
            schools: portalSchools,
            stats: {
              ...prev.stats,
              ...(portalRes.data.stats || {}),
              schools: portalSchools.length || portalRes.data.stats?.schools || 0,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to load public portal data:', err);
      }
    };
    fetchPortal();
  }, []);

  // Compute Active Modules with safe parsing
  const activeModules = useMemo<LandingModule[]>(() => {
    let mods = DEFAULT_LANDING_MODULES;
    if (portalData?.landing_page_modules) {
      try {
        const parsed =
          typeof portalData.landing_page_modules === 'string'
            ? JSON.parse(portalData.landing_page_modules)
            : portalData.landing_page_modules;
        if (Array.isArray(parsed) && parsed.length > 0) {
          const map = new Map(parsed.map((m: any) => [m.id, m]));
          mods = DEFAULT_LANDING_MODULES.map((def) => {
            const existing = map.get(def.id);
            return existing ? { ...def, ...existing } : def;
          });
          // Also include any newly added custom modules not in DEFAULT_LANDING_MODULES
          parsed.forEach((m: any) => {
            if (m.id && m.id.startsWith('custom_') && !mods.some((existing) => existing.id === m.id)) {
              mods.push(m);
            }
          });
        }
      } catch (e) {
        console.warn('Failed to parse landing_page_modules:', e);
      }
    }
    return [...mods].sort((a, b) => a.order - b.order);
  }, [portalData?.landing_page_modules]);

  const sliderAnnouncements = (Array.isArray(portalData?.announcements) ? portalData.announcements : []).slice(0, 5);

  // Auto Slider with Smooth Interval
  useEffect(() => {
    if (sliderAnnouncements.length <= 1 || isSliderHovered) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % sliderAnnouncements.length);
    }, 5500);
    return () => clearInterval(interval);
  }, [sliderAnnouncements.length, isSliderHovered]);

  // Preload & Lazy Load Slider Images (Keep initial load ultra-lightweight)
  useEffect(() => {
    if (!sliderAnnouncements.length) return;
    setLoadedSlideIndices((prev) => {
      const next = new Set(prev);
      next.add(activeSlide);
      next.add((activeSlide + 1) % sliderAnnouncements.length);
      next.add((activeSlide - 1 + sliderAnnouncements.length) % sliderAnnouncements.length);
      return Array.from(next);
    });
  }, [activeSlide, sliderAnnouncements.length]);

  // Touch Swipe Handlers for Mobile Devices
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) {
      // Swiped Left -> Next Slide
      setActiveSlide((prev) => (prev + 1) % sliderAnnouncements.length);
    } else if (distance < -minSwipeDistance) {
      // Swiped Right -> Prev Slide
      setActiveSlide((prev) => (prev - 1 + sliderAnnouncements.length) % sliderAnnouncements.length);
    }
  };

  const handleOpenSchoolsModal = async () => {
    setMobileMenuOpen(false);
    setSupportMenuAnchor(null);
    setSchoolsModalOpen(true);
    try {
      const schRes = await api.get('/schools/public');
      if (Array.isArray(schRes.data) && schRes.data.length > 0) {
        setPortalData((prev: any) => ({
          ...prev,
          schools: schRes.data,
          stats: { ...prev.stats, schools: schRes.data.length }
        }));
        return;
      }
    } catch (e1) {}
    try {
      const res = await api.get('/research/public');
      if (res.data?.schools && res.data.schools.length > 0) {
        setPortalData((prev: any) => ({
          ...prev,
          schools: res.data.schools,
          stats: { ...prev.stats, schools: res.data.schools.length }
        }));
      }
    } catch (e2) {
      console.error('Failed to load schools for modal:', e2);
    }
  };

  const getSchoolAddressString = (sch: any) => {
    if (!sch) return '-';
    if (sch.address && (sch.address.includes('จ.') || sch.address.includes('จังหวัด') || sch.address.includes('อ.') || sch.address.includes('อำเภอ'))) {
      return sch.address;
    }
    const parts = [
      sch.address,
      sch.subdistrict ? `ต.${sch.subdistrict}` : '',
      sch.district ? `อ.${sch.district}` : '',
      sch.province ? `จ.${sch.province}` : '',
      sch.postal_code || '',
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : (sch.name || '-');
  };

  const getGoogleMapRouteUrl = (sch: any, fromCurrentLocation: boolean = true) => {
    if (!sch) return 'https://www.google.com/maps';
    const destination = [
      sch.name,
      sch.subdistrict ? `ต.${sch.subdistrict}` : '',
      sch.district ? `อ.${sch.district}` : '',
      sch.province ? `จ.${sch.province}` : '',
      sch.postal_code || '',
    ].filter(Boolean).join(' ').trim();

    if (fromCurrentLocation) {
      // Omitting origin directs Google Maps to calculate route from user's current GPS location
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent('คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร')}&destination=${encodeURIComponent(destination)}`;
  };

  const handleOpenSchoolDetails = async (sch: any) => {
    setSelectedSchool(sch);
    setSchoolDetailsLoading(true);
    try {
      const res = await api.get(`/schools/public/${sch.id}`);
      if (res.data) {
        setSelectedSchool((prev: any) => ({ ...prev, ...res.data }));
      }
    } catch (e) {
      console.error('Failed to load detailed school info:', e);
    } finally {
      setSchoolDetailsLoading(false);
    }
  };

  const handleNavigateExam = () => {
    setMobileMenuOpen(false);
    setSupportMenuAnchor(null);
    if (!isLoggedIn) {
      navigate('/login?redirect=/dashboard/exams');
      return;
    }
    if (user?.role === 'STUDENT') {
      navigate('/dashboard/exams');
    } else {
      navigate('/dashboard/exams-manage');
    }
  };

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    let targetId = id;
    if (id === 'news-events' || id === 'news') {
      targetId = document.getElementById('latest_news')
        ? 'latest_news'
        : (document.getElementById('practicum_calendar') ? 'practicum_calendar' : id);
    } else if (id === 'schools-resources' || id === 'resources') {
      targetId = 'download_resources';
    } else if (id === 'schools' || id === 'partner-schools' || id === 'partner_schools') {
      targetId = 'partner_schools';
    }

    const el = document.getElementById(targetId) || document.getElementById(id);
    if (el) {
      const navHeight = 75;
      const targetPos = el.getBoundingClientRect().top + window.pageYOffset - navHeight;
      window.scrollTo({ top: Math.max(0, targetPos), behavior: 'smooth' });
    } else if (id === 'schools' || id === 'partner-schools' || id === 'partner_schools') {
      handleOpenSchoolsModal();
    }
  };

  const handleNavigateResearch = () => {
    setMobileMenuOpen(false);
    if (isLoggedIn) {
      navigate('/dashboard/research');
    } else {
      navigate('/login?redirect=/dashboard/research');
    }
  };

  const handleDownloadResource = async (resItem: any) => {
    try {
      api.put(`/resources/${resItem.id}/download`).catch(() => {});
    } catch (e) {}
    const fileUrl = resolveFileUrl(resItem.file_url || resItem.file_path);
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };


  // Filtered schools for Full Modal (Search across name, district, province, subdistrict, address)
  const filteredSchools = useMemo(() => {
    let list = portalData?.schools || [];
    if (schoolSearchQuery.trim()) {
      const q = schoolSearchQuery.toLowerCase().trim();
      list = list.filter(
        (s: any) =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.district && s.district.toLowerCase().includes(q)) ||
          (s.province && s.province.toLowerCase().includes(q)) ||
          (s.subdistrict && s.subdistrict.toLowerCase().includes(q)) ||
          (s.address && s.address.toLowerCase().includes(q))
      );
    }
    return list;
  }, [portalData?.schools, schoolSearchQuery]);

  // Filtered schools for Partner Schools Module in-card display
  const filteredModuleSchools = useMemo(() => {
    let list = portalData?.schools || [];
    if (moduleSchoolSearch.trim()) {
      const q = moduleSchoolSearch.toLowerCase().trim();
      list = list.filter(
        (s: any) =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.district && s.district.toLowerCase().includes(q)) ||
          (s.province && s.province.toLowerCase().includes(q)) ||
          (s.subdistrict && s.subdistrict.toLowerCase().includes(q)) ||
          (s.address && s.address.toLowerCase().includes(q))
      );
    }
    return list;
  }, [portalData?.schools, moduleSchoolSearch]);

  // Mentors / Co-Teachers for selected school
  const schoolCoTeacherItems = useMemo(() => {
    if (!selectedSchool) return [];
    if (Array.isArray(selectedSchool.co_teachers) && selectedSchool.co_teachers.length > 0) {
      return selectedSchool.co_teachers.map((c: any) => ({
        position: c.position || 'กลุ่มสาระการเรียนรู้ทั่วไป',
        name: `${c.prefix || ''}${c.first_name || ''} ${c.last_name || ''}`.trim(),
        phone: c.phone_number || '',
        email: c.email || '',
      }));
    }
    if (selectedSchool.co_teachers_summary) {
      return selectedSchool.co_teachers_summary.split('||').map((item: string) => {
        const [pos, name] = item.split('::');
        return { position: pos || 'กลุ่มสาระการเรียนรู้ทั่วไป', name: name || '', phone: '', email: '' };
      });
    }
    if (selectedSchool.mentor_readiness_json) {
      try {
        const parsed = typeof selectedSchool.mentor_readiness_json === 'string'
          ? JSON.parse(selectedSchool.mentor_readiness_json)
          : selectedSchool.mentor_readiness_json;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((m: any) => ({
            position: m.subject || m.grade_level || 'กลุ่มสาระการเรียนรู้ทั่วไป',
            name: m.name || '',
            phone: '',
            email: '',
          }));
        }
      } catch (e) {}
    }
    return [];
  }, [selectedSchool]);

  const schoolGroupedCoTeachers = useMemo(() => {
    const map = new Map<string, string[]>();
    schoolCoTeacherItems.forEach((item: any) => {
      const pos = item.position?.trim() || 'กลุ่มสาระการเรียนรู้ทั่วไป';
      if (!map.has(pos)) map.set(pos, []);
      if (item.name) map.get(pos)?.push(item.name);
    });
    return Array.from(map.entries());
  }, [schoolCoTeacherItems]);

  // Quotas by major for selected school
  const schoolQuotaItems = useMemo(() => {
    if (!selectedSchool) return [];
    let quotas: Record<string, any> = {};
    if (selectedSchool.major_capacities) {
      try {
        quotas = typeof selectedSchool.major_capacities === 'string'
          ? JSON.parse(selectedSchool.major_capacities)
          : selectedSchool.major_capacities;
      } catch (e) {}
    }
    if (Object.keys(quotas).length === 0 && selectedSchool.demand_json) {
      try {
        quotas = typeof selectedSchool.demand_json === 'string'
          ? JSON.parse(selectedSchool.demand_json)
          : selectedSchool.demand_json;
      } catch (e) {}
    }
    if (Object.keys(quotas).length === 0 && selectedSchool.demand?.demand_json) {
      try {
        quotas = typeof selectedSchool.demand.demand_json === 'string'
          ? JSON.parse(selectedSchool.demand.demand_json)
          : selectedSchool.demand.demand_json;
      } catch (e) {}
    }
    return Object.entries(quotas)
      .map(([major, count]) => ({ major, count: Number(count) }))
      .filter((item) => item.count > 0);
  }, [selectedSchool]);

  const schoolTotalQuota = useMemo(() => {
    if (schoolQuotaItems.length > 0) {
      return schoolQuotaItems.reduce((acc, q) => acc + q.count, 0);
    }
    return Number(selectedSchool?.capacity || 0);
  }, [schoolQuotaItems, selectedSchool]);

  // Resource categories
  const resourceCategories = useMemo(() => {
    const cats = new Map<string, string>();
    (portalData?.resources || []).forEach((r: any) => {
      if (r.category_name) {
        cats.set(r.category_name, r.category_name);
      }
    });
    return Array.from(cats.values());
  }, [portalData?.resources]);

  // Filtered resources
  const filteredResources = useMemo(() => {
    let list = portalData?.resources || [];
    if (resourceCategoryFilter !== 'ALL') {
      list = list.filter((r: any) => r.category_name === resourceCategoryFilter);
    }
    if (resourceSearchQuery.trim()) {
      const q = resourceSearchQuery.toLowerCase().trim();
      list = list.filter(
        (r: any) =>
          (r.title && r.title.toLowerCase().includes(q)) ||
          (r.description && r.description.toLowerCase().includes(q))
      );
    }
    return list;
  }, [portalData?.resources, resourceCategoryFilter, resourceSearchQuery]);

  // Separate Hero Slider from other Grid modules
  const heroModule = activeModules.find((m) => m.id === 'hero_slider' && m.enabled);
  const gridModules = activeModules.filter((m) => m.id !== 'hero_slider' && m.enabled);

  // ----------------------------------------------------
  // MODULE RENDERERS
  // ----------------------------------------------------

  // 1. HERO SLIDER & CALL TO ACTION (Ultra-Smooth Crossfade & Responsive Desktop/Mobile)
  const renderHeroSlider = (mod?: LandingModule) => {
    if (!sliderAnnouncements.length) return null;

    return (
      <Box
        id="hero"
        component="section"
        sx={{
          pt: { xs: 2, sm: 2.5, md: 3.5 },
          pb: { xs: 3, md: 4 },
          background:
            'radial-gradient(circle at 50% 20%, rgba(37, 99, 235, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
        }}
      >
        <Container maxWidth="xl">
          {/* ULTRA-SMOOTH IMAGE SLIDER (CAROUSEL) - Balanced Panoramic Banner (21:9 / 2.33:1) */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: { xs: '16 / 9', sm: '21 / 9', md: '21 / 9' },
              maxHeight: { xs: 240, sm: 300, md: 380, lg: 420 },
              minHeight: { xs: 180, sm: 220, md: 280, lg: 320 },
              borderRadius: { xs: 2.5, sm: 3.5 },
              overflow: 'hidden',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
              mb: { xs: 2.5, sm: 3 },
              bgcolor: '#0F172A',
            }}
            onMouseEnter={() => setIsSliderHovered(true)}
            onMouseLeave={() => setIsSliderHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Cross-fade Stacked Slides */}
            {sliderAnnouncements.map((slide: any, index: number) => {
              const isActive = index === activeSlide;
              const shouldShowTitle =
                slide.show_slider_title !== 0 &&
                slide.show_slider_title !== false &&
                slide.show_slider_title !== '0';

              return (
                <Box
                  key={slide.id}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    opacity: isActive ? 1 : 0,
                    visibility: isActive ? 'visible' : 'hidden',
                    transform: isActive ? 'scale(1)' : 'scale(1.04)',
                    transition:
                      'opacity 0.75s cubic-bezier(0.4, 0, 0.2, 1), transform 1.2s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.75s',
                    pointerEvents: isActive ? 'auto' : 'none',
                    zIndex: isActive ? 2 : 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/announcements/${slide.id}`)}
                >
                  {slide.banner_url && loadedSlideIndices.includes(index) ? (
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        bgcolor: '#0F172A',
                      }}
                    >
                      {/* Background Ambient Blur Layer: fills the canvas so there are NO empty/blank gaps */}
                      <Box
                        component="img"
                        src={resolveFileUrl(slide.banner_url)}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                        sx={{
                          position: 'absolute',
                          inset: -14,
                          width: 'calc(100% + 28px)',
                          height: 'calc(100% + 28px)',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          filter: 'blur(20px) brightness(0.55)',
                          transform: 'scale(1.08)',
                          pointerEvents: 'none',
                        }}
                      />
                      {/* Foreground Sharp Image: scaled & fitted perfectly (objectFit: contain) so NO content/text is cropped */}
                      <Box
                        component="img"
                        src={resolveFileUrl(slide.banner_url)}
                        alt={slide.title || 'Slide Image'}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                        // @ts-ignore
                        fetchPriority={index === 0 ? 'high' : 'low'}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          display: 'block',
                          zIndex: 1,
                        }}
                      />
                    </Box>
                  ) : slide.banner_url ? (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#0F172A' }} />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        background:
                          'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #2563EB 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        px: 3,
                      }}
                    >
                      <Megaphone
                        size={140}
                        color="#FFFFFF"
                        opacity={0.08}
                        style={{ position: 'absolute', right: 40 }}
                      />
                      <Box sx={{ textAlign: 'center', maxWidth: 800 }}>
                        <Avatar
                          src="/logo.png"
                          alt="KPRU Logo"
                          sx={{
                            width: { xs: 48, sm: 64 },
                            height: { xs: 48, sm: 64 },
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'rgba(255,255,255,0.15)',
                          }}
                        />
                        <Typography
                          variant="h4"
                          sx={{
                            color: '#FFFFFF',
                            fontWeight: 800,
                            fontFamily: "'Prompt', sans-serif",
                            fontSize: { xs: '1.2rem', sm: '1.8rem', md: '2.2rem' },
                            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                            lineHeight: 1.3,
                          }}
                        >
                          {slide.title}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Top-Right Pinned Badge when title overlay is hidden */}
                  {!shouldShowTitle && slide.is_pinned && (
                    <Box sx={{ position: 'absolute', top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, zIndex: 3 }}>
                      <Chip
                        icon={<Pin size={12} color="#fff" />}
                        label="ข่าวสำคัญ"
                        size="small"
                        sx={{
                          bgcolor: '#D97706',
                          color: '#fff',
                          fontWeight: 700,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                      />
                    </Box>
                  )}

                  {/* Bottom-Right Click Hint for mobile/desktop when title overlay is hidden */}
                  {!shouldShowTitle && slide.banner_url && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: { xs: 12, sm: 16 },
                        right: { xs: 12, sm: 16 },
                        bgcolor: 'rgba(15, 23, 42, 0.78)',
                        backdropFilter: 'blur(8px)',
                        color: '#FFFFFF',
                        px: { xs: 1.4, sm: 1.8 },
                        py: 0.6,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: { xs: '0.72rem', sm: '0.8rem' },
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                        transition: 'background-color 0.2s',
                        zIndex: 3,
                        '&:hover': { bgcolor: '#1E40AF' },
                      }}
                    >
                      <span>คลิกเพื่ออ่าน</span>
                      <ChevronRight size={14} />
                    </Box>
                  )}

                  {/* Frosted Glass Overlay with Title & Actions */}
                  {(shouldShowTitle || !slide.banner_url) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background:
                          'linear-gradient(to top, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.65) 65%, transparent 100%)',
                        p: { xs: 2, sm: 3, md: 4 },
                        color: '#FFFFFF',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        gap: 1.5,
                        zIndex: 3,
                      }}
                    >
                      <Box sx={{ maxWidth: { xs: '100%', sm: '80%' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, flexWrap: 'wrap' }}>
                          {slide.is_pinned ? (
                            <Chip
                              icon={<Pin size={11} color="#fff" />}
                              label="ข่าวสำคัญ"
                              size="small"
                              sx={{ bgcolor: '#D97706', color: '#fff', fontWeight: 700, height: 22, fontSize: '0.72rem' }}
                            />
                          ) : null}
                          <Chip
                            label={new Date(slide.created_at).toLocaleDateString('th-TH', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', height: 22, fontSize: '0.72rem' }}
                          />
                          {slide.views_count ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 0.4 }}
                            >
                              <Eye size={12} /> {slide.views_count}
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            lineHeight: 1.25,
                            fontSize: { xs: '1.05rem', sm: '1.45rem', md: '1.85rem' },
                            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                            fontFamily: "'Prompt', sans-serif",
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {slide.title}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        endIcon={<ChevronRight size={15} />}
                        sx={{
                          borderRadius: 2,
                          px: { xs: 2, sm: 2.5 },
                          py: 0.8,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          boxShadow: '0 4px 16px rgba(37, 99, 235, 0.4)',
                          flexShrink: 0,
                          display: { xs: 'none', sm: 'inline-flex' },
                        }}
                      >
                        อ่านรายละเอียด
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Navigation Arrows */}
            {sliderAnnouncements.length > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSlide(
                      (prev) => (prev - 1 + sliderAnnouncements.length) % sliderAnnouncements.length
                    );
                  }}
                  sx={{
                    position: 'absolute',
                    left: { xs: 8, sm: 16 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(15, 23, 42, 0.65)',
                    color: '#FFFFFF',
                    backdropFilter: 'blur(8px)',
                    zIndex: 4,
                    width: { xs: 34, sm: 40 },
                    height: { xs: 34, sm: 40 },
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.9)' },
                  }}
                >
                  <ChevronLeft size={22} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSlide((prev) => (prev + 1) % sliderAnnouncements.length);
                  }}
                  sx={{
                    position: 'absolute',
                    right: { xs: 8, sm: 16 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(15, 23, 42, 0.65)',
                    color: '#FFFFFF',
                    backdropFilter: 'blur(8px)',
                    zIndex: 4,
                    width: { xs: 34, sm: 40 },
                    height: { xs: 34, sm: 40 },
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.9)' },
                  }}
                >
                  <ChevronRight size={22} />
                </IconButton>

                {/* Animated Indicator Dots */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 12, sm: 16 },
                    right: { xs: 12, sm: 16 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                    bgcolor: 'rgba(15, 23, 42, 0.55)',
                    p: 0.8,
                    borderRadius: 3,
                    backdropFilter: 'blur(6px)',
                    zIndex: 4,
                  }}
                >
                  {sliderAnnouncements.map((_: any, i: number) => (
                    <Box
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSlide(i);
                      }}
                      sx={{
                        width: i === activeSlide ? 24 : 8,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: i === activeSlide ? '#60A5FA' : 'rgba(255, 255, 255, 0.4)',
                        cursor: 'pointer',
                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>

          {/* Primary Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={isLoggedIn ? <LayoutDashboard size={20} /> : <LogIn size={20} />}
              endIcon={<ArrowRight size={18} />}
              onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
              sx={{
                py: 1.5,
                px: 3.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.32)',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {isLoggedIn ? 'เข้าสู่ระบบปฏิบัติงาน (Dashboard)' : 'เข้าสู่ระบบสำหรับผู้ใช้งาน'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              color="primary"
              startIcon={<BookOpen size={20} />}
              onClick={handleNavigateResearch}
              sx={{
                py: 1.5,
                px: 3.2,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                borderWidth: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.08)',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { borderWidth: 2, bgcolor: 'rgba(239, 246, 255, 0.95)' },
              }}
            >
              สืบค้นงานวิจัยในชั้นเรียน
            </Button>

            <Button
              variant="outlined"
              size="large"
              color="inherit"
              startIcon={<Calendar size={20} />}
              onClick={() => scrollToSection('news-events')}
              sx={{
                py: 1.5,
                px: 3,
                fontSize: '1rem',
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                borderColor: '#CBD5E1',
                color: '#1E293B',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              ข่าวสาร & ปฏิทินฝึกสอน
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  };

  // 2. STATS & KPI METRICS
  const renderStatsKpi = (mod: LandingModule) => (
    <GlassCard sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Box sx={{ textAlign: 'center', mb: 2.5 }}>
        <Chip
          icon={<Sparkles size={16} />}
          label={`ปีการศึกษาปัจจุบัน ภาคเรียนที่ ${portalData?.stats?.current_semester || '1'}/${
            portalData?.stats?.current_academic_year || '2569'
          } • คณะครุศาสตร์ มรภ.กำแพงเพชร`}
          color="primary"
          variant="outlined"
          sx={{
            fontWeight: 600,
            bgcolor: 'rgba(30, 64, 175, 0.05)',
            px: 1.5,
            py: 2,
            borderRadius: 20,
            fontSize: '0.88rem',
          }}
        />
      </Box>

      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(37, 99, 235, 0.06)' }}>
            <Typography variant="h3" color="primary.main" sx={{ fontWeight: 800 }}>
              {Number(portalData?.stats?.students || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.85rem' }}>
              นักศึกษาฝึกประสบการณ์ฯ
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            onClick={handleOpenSchoolsModal}
            sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 2,
              bgcolor: 'rgba(5, 150, 105, 0.06)',
              cursor: 'pointer',
              transition: 'transform 0.18s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="h3" color="success.main" sx={{ fontWeight: 800 }}>
              {Number(portalData?.stats?.schools || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.85rem' }}>
              โรงเรียนเครือข่ายความร่วมมือ
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(217, 119, 6, 0.06)' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#D97706' }}>
              {Number(portalData?.stats?.supervisors || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.85rem' }}>
              อาจารย์นิเทศก์ประจำสาขา
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            onClick={handleNavigateResearch}
            sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 2,
              bgcolor: 'rgba(124, 58, 237, 0.06)',
              cursor: 'pointer',
              transition: 'transform 0.18s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#7C3AED' }}>
              {Number(portalData?.stats?.researches || 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.85rem' }}>
              ผลงานวิจัยในชั้นเรียน
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </GlassCard>
  );

  // 3. LATEST NEWS
  const renderLatestNews = (mod: LandingModule) => (
    <GlassCard id="latest_news" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: 'primary.main', width: 40, height: 40 }}>
            <Megaphone size={20} />
          </Avatar>
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
              ANNOUNCEMENTS
            </Typography>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              {mod.custom_title || 'ข่าวประชาสัมพันธ์และประกาศล่าสุด'}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowRight size={15} />}
          onClick={() => navigate('/announcements')}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          ดูทั้งหมด
        </Button>
      </Box>

      <Stack spacing={1.8} sx={{ flexGrow: 1 }}>
        {(Array.isArray(portalData?.announcements) ? portalData.announcements : [])
          .slice(0, 10)
          .map((ann: any) => (
            <Paper
              key={ann.id}
              variant="outlined"
              onClick={() => navigate(`/announcements/${ann.id}`)}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.18s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(30, 64, 175, 0.02)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                {ann.banner_url && (
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 75,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: '#F1F5F9',
                      }}
                    >
                      <Box
                        component="img"
                        src={resolveFileUrl(ann.banner_url)}
                        alt={ann.title}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: ann.banner_url ? 9 : 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      {ann.is_pinned ? (
                        <Chip
                          icon={<Pin size={10} />}
                          label="สำคัญ"
                          size="small"
                          sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700, height: 18, fontSize: '0.68rem' }}
                        />
                      ) : null}
                      <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {ann.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={new Date(ann.created_at).toLocaleDateString('th-TH', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                      size="small"
                      sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.72rem', height: 20 }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.84rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {ann.content ? ann.content.replace(/<[^>]*>?/gm, '').trim() : ''}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          ))}
        {(!Array.isArray(portalData?.announcements) || portalData.announcements.length === 0) && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>ยังไม่มีประกาศข่าวสารใหม่ในขณะนี้</Box>
        )}
      </Stack>
    </GlassCard>
  );

  // 4. PRACTICUM CALENDAR
  const renderPracticumCalendar = (mod: LandingModule) => (
    <GlassCard id="practicum_calendar" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
        <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.12)', color: 'secondary.main', width: 40, height: 40 }}>
          <Calendar size={20} />
        </Avatar>
        <Box>
          <Typography variant="overline" color="secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
            PRACTICUM SCHEDULE
          </Typography>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            {mod.custom_title || 'ปฏิทินกำหนดการฝึกประสบการณ์ฯ'}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2} sx={{ flexGrow: 1 }}>
        {(Array.isArray(portalData?.events) ? portalData.events : []).map((ev: any) => (
          <Paper
            key={ev.id}
            variant="outlined"
            onClick={() => setSelectedEvent(ev)}
            sx={{
              p: 1.8,
              borderRadius: 2,
              display: 'flex',
              gap: 1.8,
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: '#F8FAFC',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.08)',
              },
            }}
          >
            <Box
              sx={{
                minWidth: 54,
                p: 0.8,
                borderRadius: 2,
                bgcolor: '#EFF6FF',
                color: '#1E40AF',
                textAlign: 'center',
                border: '1px solid #BFDBFE',
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {new Date(ev.event_date).getDate()}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 0.2 }}>
                {new Date(ev.event_date).toLocaleDateString('th-TH', { month: 'short' })}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3, flexWrap: 'wrap' }}>
                {ev.event_type && (
                  <Chip
                    label={ev.event_type}
                    size="small"
                    sx={{ height: 18, fontSize: '0.68rem', bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }}
                  />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                  {ev.title}
                </Typography>
              </Box>
              {ev.description && (
                <Box
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    '& p': { m: 0, p: 0 },
                    '& strong': { color: 'text.primary', fontWeight: 600 },
                    '& a': { color: '#1E40AF', textDecoration: 'underline' },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: ev.description
                      .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                      .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
                  }}
                />
              )}
            </Box>
          </Paper>
        ))}
        {(!Array.isArray(portalData?.events) || portalData.events.length === 0) && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>ไม่มีกำหนดการเร็วๆ นี้</Box>
        )}
      </Stack>
    </GlassCard>
  );

  // 5. DOWNLOADABLE RESOURCES
  const renderDownloadResources = (mod: LandingModule) => (
    <GlassCard id="download_resources" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: 'primary.main', width: 40, height: 40 }}>
            <FolderOpen size={20} />
          </Avatar>
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
              DOWNLOAD CENTER
            </Typography>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              {mod.custom_title || 'คลังเอกสาร คู่มือ และแบบฟอร์ม'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="ค้นหาเอกสาร..."
            value={resourceSearchQuery}
            onChange={(e) => setResourceSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 190 }, bgcolor: '#FFFFFF' }}
            slotProps={{
              input: {
                startAdornment: <Search size={15} color="#64748B" style={{ marginRight: 6 }} />,
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<FolderOpen size={15} />}
            onClick={() => setResourcesModalOpen(true)}
            sx={{ borderRadius: 2, fontWeight: 700, height: 36, whiteSpace: 'nowrap' }}
          >
            ดูทั้งหมด ({filteredResources.length})
          </Button>
        </Box>
      </Box>

      {/* Category Filter Chips */}
      {resourceCategories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
          <Chip
            label="ทั้งหมด"
            onClick={() => setResourceCategoryFilter('ALL')}
            color={resourceCategoryFilter === 'ALL' ? 'primary' : 'default'}
            variant={resourceCategoryFilter === 'ALL' ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {resourceCategories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setResourceCategoryFilter(cat)}
              color={resourceCategoryFilter === cat ? 'primary' : 'default'}
              variant={resourceCategoryFilter === cat ? 'filled' : 'outlined'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {/* Showing First 5 Items */}
      <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
        {filteredResources.slice(0, 5).map((resItem: any) => (
          <Paper
            key={resItem.id}
            variant="outlined"
            sx={{
              p: 1.8,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              '&:hover': { borderColor: 'primary.main', bgcolor: '#F8FAFC' },
            }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                <Chip
                  label={resItem.category_name || 'เอกสาร'}
                  size="small"
                  sx={{ height: 18, fontSize: '0.68rem', bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }}
                />
                {resItem.download_count > 0 && (
                  <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Download size={11} /> {resItem.download_count}
                  </Typography>
                )}
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                {resItem.title}
              </Typography>
            </Box>
            {resItem.file_url && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleDownloadResource(resItem)}
                startIcon={<Download size={14} />}
                sx={{ flexShrink: 0, borderRadius: 1.8, fontWeight: 700 }}
              >
                ดาวน์โหลด
              </Button>
            )}
          </Paper>
        ))}
        {filteredResources.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>ไม่พบเอกสารดาวน์โหลด</Box>
        )}
      </Stack>

      {filteredResources.length > 5 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => setResourcesModalOpen(true)}
            endIcon={<ArrowRight size={15} />}
            sx={{ fontWeight: 700 }}
          >
            ดูรายการเอกสารทั้งหมด ({filteredResources.length} รายการ)
          </Button>
        </Box>
      )}
    </GlassCard>
  );

  // 6. PARTNER SCHOOLS DIRECTORY (In-Card Table & Direct Google Maps Route)
  const renderPartnerSchools = (mod: LandingModule) => (
    <GlassCard id="partner_schools" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: 'rgba(5, 150, 105, 0.12)', color: 'success.main', width: 40, height: 40 }}>
            <Building2 size={20} />
          </Avatar>
          <Box>
            <Typography variant="overline" color="success.main" sx={{ fontWeight: 700, lineHeight: 1 }}>
              PARTNER SCHOOLS
            </Typography>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              {mod.custom_title || 'ข้อมูลโรงเรียนเครือข่ายความร่วมมือฝึกประสบการณ์วิชาชีพครู'}
            </Typography>
          </Box>
        </Box>

        {/* Quick Search */}
        <Box sx={{ width: { xs: '100%', sm: 260 }, mt: { xs: 0.5, sm: 0 } }}>
          <TextField
            size="small"
            fullWidth
            placeholder="ค้นหาชื่อโรงเรียน/อำเภอ/ที่อยู่..."
            value={moduleSchoolSearch}
            onChange={(e) => setModuleSchoolSearch(e.target.value)}
            sx={{ bgcolor: '#FFFFFF' }}
            slotProps={{
              input: {
                startAdornment: <Search size={15} color="#64748B" style={{ marginRight: 6 }} />,
              },
            }}
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        ข้อมูลโรงเรียนเครือข่ายความร่วมมือฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มรภ.กำแพงเพชร (คลิกที่ชื่อโรงเรียนเพื่อดูรายละเอียด สวัสดิการ โควตารับ และครูพี่เลี้ยง)
      </Typography>

      {/* Schools Table Directly In-Card */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 420, flexGrow: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 700, width: 55, textAlign: 'center' }}>ลำดับ</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ชื่อสถานศึกษา (คลิกเพื่อดูรายละเอียด)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ที่อยู่ / อำเภอ - จังหวัด</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 95 }}>แผนที่นำทาง</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredModuleSchools.map((sch: any, index: number) => {
              const routeUrl = getGoogleMapRouteUrl(sch, true);
              return (
                <TableRow
                  key={sch.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOpenSchoolDetails(sch)}
                >
                  <TableCell sx={{ textAlign: 'center', color: '#64748B', fontWeight: 600 }}>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.8,
                        color: '#1E40AF',
                        fontWeight: 700,
                        '&:hover': { color: '#2563EB', textDecoration: 'underline' },
                      }}
                      title="คลิกเพื่อดูรายละเอียดโรงเรียน"
                    >
                      <Building2 size={15} color="#2563EB" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'inherit' }}>
                        {sch.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#334155' }}>
                      {getSchoolAddressString(sch)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      href={routeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<Navigation size={12} />}
                      sx={{ borderRadius: 1.8, fontSize: '0.72rem', py: 0.3, px: 1, fontWeight: 700 }}
                    >
                      นำทาง
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredModuleSchools.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 5, color: '#64748B' }}>
                  ไม่พบข้อมูลโรงเรียนเครือข่าย
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </GlassCard>
  );

  // 7. CUSTOM MODULE RENDERER (Rich Text, Banner, Quick Links, Video)
  const renderCustomModule = (mod: LandingModule) => {
    switch (mod.type) {
      case 'custom_banner':
        return (
          <GlassCard sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {mod.banner_url && (
              <Box
                component="img"
                src={resolveFileUrl(mod.banner_url)}
                alt={mod.custom_title || mod.name}
                sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 2, mb: 2 }}
              />
            )}
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
              {mod.custom_title || mod.name}
            </Typography>
            {mod.custom_subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mod.custom_subtitle}
              </Typography>
            )}
            {mod.banner_link_url && (
              <Box sx={{ mt: 'auto', pt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  href={mod.banner_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={15} />}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  {mod.banner_cta_text || 'อ่านเพิ่มเติม'}
                </Button>
              </Box>
            )}
          </GlassCard>
        );

      case 'custom_links':
        return (
          <GlassCard sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(5, 150, 105, 0.12)', color: '#059669', width: 38, height: 38 }}>
                <LinkIcon size={18} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                  {mod.custom_title || mod.name}
                </Typography>
                {mod.custom_subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {mod.custom_subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <Grid container spacing={1.5} sx={{ flexGrow: 1 }}>
              {(mod.links || []).map((link) => (
                <Grid size={{ xs: 12, sm: 6 }} key={link.id}>
                  <Paper
                    variant="outlined"
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      p: 1.8,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textDecoration: 'none',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(30, 64, 175, 0.04)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {link.title}
                      </Typography>
                      {link.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {link.subtitle}
                        </Typography>
                      )}
                    </Box>
                    <ExternalLink size={16} color="#64748B" />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </GlassCard>
        );

      case 'custom_video':
        return (
          <GlassCard sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
              {mod.custom_title || mod.name}
            </Typography>
            {mod.custom_subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mod.custom_subtitle}
              </Typography>
            )}
            {mod.video_url && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  pt: '56.25%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: '#000',
                }}
              >
                <Box
                  component="iframe"
                  src={mod.video_url}
                  title={mod.custom_title || mod.name}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}
          </GlassCard>
        );

      case 'custom_html':
      default:
        return (
          <GlassCard sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
              {mod.custom_title || mod.name}
            </Typography>
            {mod.custom_subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mod.custom_subtitle}
              </Typography>
            )}
            <Box
              sx={{
                color: '#334155',
                lineHeight: 1.7,
                fontSize: '0.95rem',
                '& p': { mb: 1.5 },
                '& a': { color: '#1E40AF', textDecoration: 'underline' },
              }}
              dangerouslySetInnerHTML={{
                __html: (mod.content_html || '')
                  .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                  .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
              }}
            />
          </GlassCard>
        );
    }
  };

  // Render module switch
  const renderModuleContent = (mod: LandingModule) => {
    switch (mod.id) {
      case 'stats_kpi':
        return renderStatsKpi(mod);
      case 'latest_news':
        return renderLatestNews(mod);
      case 'practicum_calendar':
        return renderPracticumCalendar(mod);
      case 'download_resources':
        return renderDownloadResources(mod);
      case 'partner_schools':
        return renderPartnerSchools(mod);
      default:
        return renderCustomModule(mod);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
      {/* SEO JSON-LD Structured Data for Google Search Console */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'EducationalOrganization',
            name: 'ฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร (TPMS KPRU)',
            alternateName: [
              'TPMS KPRU',
              'Teaching Practicum Management System KPRU',
              'ระบบฝึกประสบการณ์วิชาชีพครู มรภ.กำแพงเพชร',
            ],
            url: 'https://tp.kpru.ac.th/',
            logo: 'https://tp.kpru.ac.th/logo.png',
            description:
              'ระบบบริหารจัดการงานฝึกประสบการณ์วิชาชีพครู - Teacher Practicum Management System (TPMS) คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'เมืองกำแพงเพชร',
              addressRegion: 'กำแพงเพชร',
              postalCode: '62000',
              addressCountry: 'TH',
            },
          }),
        }}
      />

      {/* Sticky Navigation Header (PTLE Frosted Glass Standard) */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
          color: '#0F172A',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: 70 }}>
            {/* Brand Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() => scrollToSection('hero')}
            >
              <Avatar
                src="/logo.png"
                alt="TPMS KPRU"
                sx={{
                  bgcolor: 'primary.main',
                  width: 44,
                  height: 44,
                  p: 0.4,
                  boxShadow: '0 4px 10px rgba(30, 64, 175, 0.22)',
                }}
              >
                <GraduationCap size={24} color="#fff" />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, letterSpacing: -0.5, color: '#1E3A8A', lineHeight: 1.1 }}
                  >
                    TPMS
                  </Typography>
                  <Chip
                    label="KPRU"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(217, 119, 6, 0.12)',
                      color: '#B45309',
                      fontWeight: 700,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: '#64748B', display: { xs: 'none', sm: 'block' }, lineHeight: 1.1 }}
                >
                  คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Button
                color="inherit"
                startIcon={<Megaphone size={16} />}
                onClick={() => scrollToSection('news-events')}
                sx={{ fontWeight: 500, color: '#475569', borderRadius: 2, '&:hover': { color: 'primary.main' } }}
              >
                ข่าวสารและปฏิทิน
              </Button>
              <Button
                color="inherit"
                startIcon={<FolderOpen size={16} />}
                onClick={() => scrollToSection('schools-resources')}
                sx={{ fontWeight: 500, color: '#475569', borderRadius: 2, '&:hover': { color: 'primary.main' } }}
              >
                คลังเอกสาร & คู่มือ
              </Button>
              <Button
                color="inherit"
                startIcon={<GraduationCap size={17} />}
                endIcon={<ChevronDown size={14} />}
                onClick={(e) => setSupportMenuAnchor(e.currentTarget)}
                sx={{
                  fontWeight: 600,
                  color: supportMenuAnchor ? 'primary.main' : '#1E40AF',
                  borderRadius: 2,
                  px: 1.5,
                  bgcolor: supportMenuAnchor ? 'rgba(30, 64, 175, 0.08)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.06)' },
                }}
              >
                ระบบสนับสนุน
              </Button>

              {/* Student Support Dropdown Menu */}
              <Menu
                anchorEl={supportMenuAnchor}
                open={Boolean(supportMenuAnchor)}
                onClose={() => setSupportMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1.2,
                      minWidth: 260,
                      borderRadius: 2.5,
                      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)',
                      border: '1px solid #E2E8F0',
                      p: 0.8,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setSupportMenuAnchor(null);
                    handleOpenSchoolsModal();
                  }}
                  sx={{ borderRadius: 1.8, py: 1, gap: 1.2 }}
                >
                  <Building2 size={18} color="#059669" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      ข้อมูลโรงเรียนเครือข่ายฯ
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      ข้อมูลโรงเรียนเครือข่ายความร่วมมือ & แผนที่นำทาง
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setSupportMenuAnchor(null);
                    window.open('https://tp.kpru.ac.th/ptle/', '_blank');
                  }}
                  sx={{ borderRadius: 1.8, py: 1, gap: 1.2 }}
                >
                  <Award size={18} color="#D97706" />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      ระบบทดสอบและประเมินสมรรถนะฯ (PTLE)
                      <ExternalLink size={12} color="#94A3B8" />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      ประเมินสมรรถนะวิชาชีพครู คณะครุศาสตร์
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setSupportMenuAnchor(null);
                    handleNavigateResearch();
                  }}
                  sx={{ borderRadius: 1.8, py: 1, gap: 1.2 }}
                >
                  <BookOpen size={18} color="#1E40AF" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      ระบบสืบค้นงานวิจัยในชั้นเรียน
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      คลังผลงานวิจัย บทความวิจัยในชั้นเรียน
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setSupportMenuAnchor(null);
                    handleNavigateExam();
                  }}
                  sx={{ borderRadius: 1.8, py: 1, gap: 1.2 }}
                >
                  <FileQuestion size={18} color="#7C3AED" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      ระบบสอบประมวลผลความรู้
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      ระบบสอบและรายงานสถิติผลการสอบ
                    </Typography>
                  </Box>
                </MenuItem>
              </Menu>
            </Box>

            {/* Action CTA + Mobile Hamburger */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isStaffOrAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<Sliders size={16} />}
                  onClick={() => navigate('/dashboard/landing-page')}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    borderRadius: 2,
                    fontWeight: 700,
                    borderColor: 'rgba(30, 64, 175, 0.4)',
                  }}
                >
                  จัดการหน้าแรก (Builder)
                </Button>
              )}

              {isLoggedIn ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LayoutDashboard size={18} />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ fontWeight: 600, borderRadius: 2.5, px: { xs: 1.8, sm: 2.5 } }}
                >
                  แดชบอร์ด ({user.username})
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LogIn size={18} />}
                  onClick={() => navigate('/login')}
                  sx={{ fontWeight: 600, borderRadius: 2.5, px: { xs: 1.8, sm: 2.5 } }}
                >
                  เข้าสู่ระบบ
                </Button>
              )}

              <IconButton
                color="inherit"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ display: { md: 'none' }, color: '#0F172A' }}
                aria-label="เปิดเมนูนำทาง"
              >
                <MenuIcon size={22} />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          slotProps={{ paper: { sx: { width: 280, p: 2 } } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src="/logo.png" sx={{ width: 36, height: 36, bgcolor: 'primary.main' }} />
              <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 700 }}>
                TPMS KPRU
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setMobileMenuOpen(false)}>
              <X size={20} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            <ListItem disablePadding>
              <ListItemButton onClick={() => scrollToSection('news-events')} sx={{ borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Megaphone size={18} color="#1E40AF" />
                </ListItemIcon>
                <ListItemText primary="ข่าวสารและปฏิทิน" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => scrollToSection('schools-resources')} sx={{ borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <FolderOpen size={18} color="#1E40AF" />
                </ListItemIcon>
                <ListItemText primary="คลังเอกสาร & คู่มือ" />
              </ListItemButton>
            </ListItem>

            {/* Support Services Sub-menu */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setMobileSupportOpen((prev) => !prev)}
                sx={{ borderRadius: 2, bgcolor: mobileSupportOpen ? '#EFF6FF' : 'transparent' }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <GraduationCap size={18} color="#1E40AF" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E40AF' }}>
                      ระบบสนับสนุน
                    </Typography>
                  }
                />
                <ChevronDown
                  size={16}
                  style={{
                    transform: mobileSupportOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <Collapse in={mobileSupportOpen} timeout="auto" unmountOnExit>
              <List dense sx={{ pl: 2 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleOpenSchoolsModal();
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Building2 size={16} color="#059669" />
                    </ListItemIcon>
                    <ListItemText primary="ข้อมูลโรงเรียนเครือข่ายฯ" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.open('https://tp.kpru.ac.th/ptle/', '_blank');
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Award size={16} color="#D97706" />
                    </ListItemIcon>
                    <ListItemText primary="ระบบทดสอบสมรรถนะฯ (PTLE)" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleNavigateResearch}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <BookOpen size={16} color="#1E40AF" />
                    </ListItemIcon>
                    <ListItemText primary="ระบบสืบค้นงานวิจัยในชั้นเรียน" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleNavigateExam}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <FileQuestion size={16} color="#7C3AED" />
                    </ListItemIcon>
                    <ListItemText primary="ระบบสอบประมวลผลความรู้" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
            {isStaffOrAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard/landing-page');
                  }}
                  sx={{ borderRadius: 2, bgcolor: '#EFF6FF', mt: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Sliders size={18} color="#1E40AF" />
                  </ListItemIcon>
                  <ListItemText primary="จัดการหน้าแรก (Builder)" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Drawer>
      </AppBar>

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* HERO CAROUSEL SECTION */}
        {renderHeroSlider(heroModule)}

        {/* CSS GRID LAYOUT SECTIONS CONTAINER */}
        <Container maxWidth="xl" sx={{ pb: 8 }}>
          <Grid container spacing={3.5} sx={{ alignItems: 'stretch' }}>
            {gridModules.map((mod) => {
              const cols = mod.grid_columns || 12;
              return (
                <Grid
                  key={mod.id}
                  id={mod.id}
                  size={{ xs: 12, md: cols }}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  {renderModuleContent(mod)}
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Quick Landing Page Builder FAB for Admin / Staff */}
      {isStaffOrAdmin && (
        <Tooltip title="ระบบจัดการและจัดเรียงหน้าแรก (Landing Page Builder)" arrow placement="left">
          <Fab
            color="primary"
            onClick={() => navigate('/dashboard/landing-page')}
            sx={{
              position: 'fixed',
              bottom: 28,
              right: 28,
              zIndex: 1200,
              boxShadow: '0 8px 24px rgba(30, 64, 175, 0.4)',
              fontWeight: 700,
              textTransform: 'none',
              gap: 1,
              px: 2.5,
              width: 'auto',
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <Sliders size={20} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#fff', display: { xs: 'none', sm: 'block' } }}>
              จัดการหน้าแรก (Builder)
            </Typography>
          </Fab>
        </Tooltip>
      )}

      {/* PARTNER SCHOOLS DIRECTORY MODAL */}
      <Dialog
        open={schoolsModalOpen}
        onClose={() => setSchoolsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3, maxHeight: '90vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #E2E8F0',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(5, 150, 105, 0.12)', color: 'success.main', width: 42, height: 42 }}>
              <Building2 size={22} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                ข้อมูลโรงเรียนเครือข่ายความร่วมมือฝึกประสบการณ์วิชาชีพครู
              </Typography>
              <Typography variant="caption" color="text.secondary">
                คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร (ทั้งหมด {Number(portalData?.schools?.length || 0)} โรงเรียน)
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSchoolsModalOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Search Bar - Positioned with proper spacing */}
          <Box sx={{ mt: 1.5, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="ค้นหาชื่อโรงเรียน, อำเภอ, จังหวัด หรือที่อยู่..."
              value={schoolSearchQuery}
              onChange={(e) => setSchoolSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#64748B" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Results Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
              พบข้อมูล {filteredSchools.length.toLocaleString()} โรงเรียน (คลิกที่ชื่อโรงเรียนเพื่อดูข้อมูลสวัสดิการ โควตา และครูพี่เลี้ยง)
            </Typography>
            {schoolSearchQuery && (
              <Button
                size="small"
                onClick={() => setSchoolSearchQuery('')}
              >
                ล้างคำค้นหา
              </Button>
            )}
          </Box>

          {/* Schools Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 480 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, width: 60, textAlign: 'center' }}>ลำดับ</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ชื่อสถานศึกษา (คลิกเพื่อดูรายละเอียด)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ที่อยู่ / อำเภอ - จังหวัด</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 110 }}>แผนที่ / นำทาง</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSchools.map((sch: any, index: number) => {
                  const routeUrl = getGoogleMapRouteUrl(sch, true);
                  return (
                    <TableRow
                      key={sch.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenSchoolDetails(sch)}
                    >
                      <TableCell sx={{ textAlign: 'center', color: '#64748B', fontWeight: 600 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.8,
                            color: '#1E40AF',
                            fontWeight: 700,
                            '&:hover': { color: '#2563EB', textDecoration: 'underline' },
                          }}
                          title="คลิกเพื่อดูรายละเอียดโรงเรียน"
                        >
                          <Building2 size={16} color="#2563EB" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'inherit' }}>
                            {sch.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          {getSchoolAddressString(sch)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          href={routeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<Navigation size={13} />}
                          sx={{ borderRadius: 1.8, fontSize: '0.75rem', py: 0.3, px: 1.2, fontWeight: 700 }}
                        >
                          นำทาง
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredSchools.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 5, color: '#64748B' }}>
                      ไม่พบโรงเรียนที่ตรงกับเงื่อนไขการค้นหา
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setSchoolsModalOpen(false)} variant="contained" color="inherit">
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* FULL DOWNLOADABLE RESOURCES MODAL */}
      <Dialog
        open={resourcesModalOpen}
        onClose={() => setResourcesModalOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: 'primary.main', width: 44, height: 44 }}>
              <FolderOpen size={24} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                คลังเอกสาร คู่มือ และแบบฟอร์ม ทั้งหมด
              </Typography>
              <Typography variant="caption" color="text.secondary">
                เอกสารประกอบการฝึกประสบการณ์วิชาชีพครู มหาวิทยาลัยราชภัฏกำแพงเพชร
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setResourcesModalOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {/* Search & Filter */}
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="ค้นหาชื่อเอกสาร คำอธิบาย หรือประเภทเอกสาร..."
              value={resourceSearchQuery}
              onChange={(e) => setResourceSearchQuery(e.target.value)}
              fullWidth
              sx={{ bgcolor: '#FFFFFF', mb: 1.5 }}
              slotProps={{
                input: {
                  startAdornment: <Search size={16} color="#64748B" style={{ marginRight: 8 }} />,
                },
              }}
            />

            {resourceCategories.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                <Chip
                  label="ทั้งหมด"
                  onClick={() => setResourceCategoryFilter('ALL')}
                  color={resourceCategoryFilter === 'ALL' ? 'primary' : 'default'}
                  variant={resourceCategoryFilter === 'ALL' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                {resourceCategories.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    onClick={() => setResourceCategoryFilter(cat)}
                    color={resourceCategoryFilter === cat ? 'primary' : 'default'}
                    variant={resourceCategoryFilter === cat ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Resources List */}
          <Stack spacing={1.5} sx={{ maxHeight: 460, overflowY: 'auto' }}>
            {filteredResources.map((resItem: any) => (
              <Paper
                key={resItem.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.5,
                  '&:hover': { borderColor: 'primary.main', bgcolor: '#F8FAFC' },
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                    <Chip
                      label={resItem.category_name || 'เอกสาร'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }}
                    />
                    {resItem.download_count > 0 && (
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Download size={12} /> {resItem.download_count} ครั้ง
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                    {resItem.title}
                  </Typography>
                  {resItem.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                      {resItem.description}
                    </Typography>
                  )}
                </Box>
                {resItem.file_url && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleDownloadResource(resItem)}
                    startIcon={<Download size={15} />}
                    sx={{ flexShrink: 0, borderRadius: 2, fontWeight: 700 }}
                  >
                    ดาวน์โหลด
                  </Button>
                )}
              </Paper>
            ))}
            {filteredResources.length === 0 && (
              <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                ไม่พบเอกสารที่ตรงกับเงื่อนไขการค้นหา
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResourcesModalOpen(false)} variant="contained" color="inherit">
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* EVENT DETAILS MODAL */}
      <Dialog
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.12)', color: 'secondary.main', width: 44, height: 44 }}>
              <Calendar size={24} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.3 }}>
                {selectedEvent?.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={
                    selectedEvent?.event_date
                      ? new Date(selectedEvent.event_date).toLocaleDateString('th-TH', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })
                      : ''
                  }
                  size="small"
                  sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700, fontSize: '0.75rem' }}
                />
                {selectedEvent?.event_type && (
                  <Chip
                    label={selectedEvent.event_type}
                    size="small"
                    sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700, fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSelectedEvent(null)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {selectedEvent?.description ? (
            <Box
              sx={{
                color: '#334155',
                fontSize: '0.95rem',
                lineHeight: 1.7,
                '& p': { mb: 1.5 },
                '& strong': { color: '#0F172A' },
                '& a': { color: '#1E40AF', textDecoration: 'underline' },
                '& ul, & ol': { pl: 2.5, mb: 1.5 },
                '& img': { maxWidth: '100%', borderRadius: 2, my: 1 },
              }}
              dangerouslySetInnerHTML={{
                __html: selectedEvent.description
                  .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                  .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              ไม่มีรายละเอียดเพิ่มเติมสำหรับกำหนดการนี้
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedEvent(null)} variant="contained" color="inherit">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------------- */}
      {/* SCHOOL DETAILS DIALOG (ข้อมูลโรงเรียนเครือข่าย, สวัสดิการ, ครูพี่เลี้ยง, โควตา) */}
      {/* ------------------------------------------------------------------------- */}
      <Dialog
        open={Boolean(selectedSchool)}
        onClose={() => setSelectedSchool(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3.5,
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            bgcolor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            p: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
            <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: '#1E40AF', width: 48, height: 48 }}>
              <Building2 size={26} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.3 }}>
                {selectedSchool?.name || 'รายละเอียดโรงเรียน'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                <Chip
                  label={selectedSchool?.affiliation || 'สังกัด สพฐ.'}
                  size="small"
                  sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700, fontSize: '0.72rem' }}
                />
                {selectedSchool?.province && (
                  <Chip
                    icon={<MapPin size={12} />}
                    label={`จังหวัด${selectedSchool.province}`}
                    size="small"
                    sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600, fontSize: '0.72rem' }}
                  />
                )}
                {selectedSchool?.distance_from_university_km && (
                  <Chip
                    icon={<Compass size={12} />}
                    label={`${selectedSchool.distance_from_university_km} กม. จาก มรภ.กำแพงเพชร`}
                    size="small"
                    sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.72rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSelectedSchool(null)} sx={{ color: '#64748B' }}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        {schoolDetailsLoading && <LinearProgress sx={{ height: 2 }} />}

        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFAFA' }}>
          <Stack spacing={2.5}>
            {/* 1. General Info & Welfare */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, bgcolor: '#FFFFFF' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E40AF', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info size={16} />
                ข้อมูลทั่วไปและสวัสดิการ
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
                    ที่ตั้งสถานศึกษา
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, mt: 0.2 }}>
                    {getSchoolAddressString(selectedSchool)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
                    ข้อมูลติดต่อ
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1E293B', mt: 0.2, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Phone size={14} color="#2563EB" />
                    {selectedSchool?.phone || '-'}
                  </Typography>
                  {selectedSchool?.email && (
                    <Typography variant="body2" sx={{ color: '#1E293B', mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Mail size={14} color="#2563EB" />
                      {selectedSchool.email}
                    </Typography>
                  )}
                </Grid>

                {/* Welfare Info */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: selectedSchool?.has_accommodation === 1 || selectedSchool?.has_accommodation === 'มี' ? '#DCFCE7' : '#F1F5F9', color: selectedSchool?.has_accommodation === 1 || selectedSchool?.has_accommodation === 'มี' ? '#166534' : '#64748B' }}>
                      <Home size={16} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                        ที่พักรับรองสำหรับนักศึกษา
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        {selectedSchool?.has_accommodation === 1 || selectedSchool?.has_accommodation === 'มี'
                          ? (selectedSchool?.accommodation_details ? `มีที่พัก (${selectedSchool.accommodation_details})` : 'มีที่พักรับรองสำหรับนักศึกษา')
                          : 'ไม่มีที่พักรับรอง'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: selectedSchool?.has_lunch === 1 || selectedSchool?.has_lunch === 'มี' ? '#DCFCE7' : '#F1F5F9', color: selectedSchool?.has_lunch === 1 || selectedSchool?.has_lunch === 'มี' ? '#166534' : '#64748B' }}>
                      <Utensils size={16} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                        สวัสดิการอาหารกลางวัน
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        {selectedSchool?.has_lunch === 1 || selectedSchool?.has_lunch === 'มี' ? 'มีอาหารกลางวัน' : 'ไม่มีอาหารกลางวัน'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {selectedSchool?.welfare_notes && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 1.2, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px dashed #CBD5E1' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
                        หมายเหตุสวัสดิการเพิ่มเติม:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        {selectedSchool.welfare_notes}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* 2. Co-Teachers Grouped by Subject */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, bgcolor: '#FFFFFF' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Users size={16} />
                  จำนวนครูพี่เลี้ยง แยกตามกลุ่มสาระการเรียนรู้
                </Typography>
                <Chip
                  label={`รวมทั้งหมด ${schoolCoTeacherItems.length} คน`}
                  size="small"
                  sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700, fontSize: '0.72rem' }}
                />
              </Box>

              {schoolGroupedCoTeachers.length > 0 ? (
                <Grid container spacing={1.5}>
                  {schoolGroupedCoTeachers.map(([position, names]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={position}>
                      <Box sx={{ p: 1.2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                            {position}
                          </Typography>
                          <Chip label={`${names.length} คน`} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.5 }}>
                          {names.join(', ')}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2.5, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    อยู่ระหว่างการบันทึกข้อมูลครูพี่เลี้ยงประจำปีการศึกษา
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* 3. Student Teacher Quotas by Major */}
            <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, bgcolor: '#FFFFFF' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GraduationCap size={16} />
                  จำนวนโควตานักศึกษาที่เปิดรับ
                </Typography>
                <Chip
                  label={`เปิดรับรวม ${schoolTotalQuota} คน`}
                  size="small"
                  sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700, fontSize: '0.72rem' }}
                />
              </Box>

              {schoolQuotaItems.length > 0 ? (
                <Grid container spacing={1.2}>
                  {schoolQuotaItems.map(({ major, count }) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={major}>
                      <Box sx={{ p: 1, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534' }}>
                          {major}
                        </Typography>
                        <Chip label={`${count} คน`} size="small" sx={{ bgcolor: '#166534', color: '#FFFFFF', fontWeight: 700, height: 20, fontSize: '0.68rem' }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2.5, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    {selectedSchool?.capacity
                      ? `เปิดรับรวมทั้งหมด ${selectedSchool.capacity} คน (เปิดรับทุกสาขาวิชาตามประกาศคณะครุศาสตร์)`
                      : 'อยู่ระหว่างการจัดสรรโควตานักศึกษาประจำปีการศึกษา'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.2, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              href={getGoogleMapRouteUrl(selectedSchool, true)}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Navigation size={16} />}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              นำทางจากตำแหน่งปัจจุบัน
            </Button>
            <Button
              variant="outlined"
              color="primary"
              href={getGoogleMapRouteUrl(selectedSchool, false)}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<MapPin size={16} />}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              นำทางจาก มรภ.กำแพงเพชร
            </Button>
          </Box>
          <Button onClick={() => setSelectedSchool(null)} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      {/* PTLE Standard Dark Slate Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage;
